import { useState } from 'react';
import {
  Alert, Box, Card, CardContent, CardHeader, CircularProgress, Divider,
  Stack, Switch, Typography,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockClockIcon from '@mui/icons-material/LockClock';
import SectionHeader from '../../components/shared/SectionHeader';
import StatusBadge from '../../components/shared/StatusBadge';
import lockDeskApi from '../../services/lockDeskApi';
import { useToast } from '../../components/shared/Toast';
import { useConfirmModal } from '../../components/shared/ConfirmModal';
import { getAxiosErrorMessage } from '../../utils/getAxiosErrorMessage';

/**
 * LockDeskSettingsPage — Lender admin control to open/close the Lock Desk.
 * When closed, lock submission is blocked across the app (pro-api enforces
 * server-side; the UI disables lock actions and shows messaging).
 */
const LockDeskSettingsPage = () => {
  const { data: status, isLoading, isError } = lockDeskApi.endpoints.getLockDeskStatus.useQuery();
  const [updateLockDeskStatus] = lockDeskApi.endpoints.updateLockDeskStatus.useMutation();
  const [isSaving, setIsSaving] = useState(false);
  const { showToast, ToastContainer } = useToast();
  const { confirm, ConfirmModalContainer } = useConfirmModal();

  const isEnabled = status?.isEnabled !== false;

  const handleToggle = async () => {
    const turningOff = isEnabled;
    const confirmed = await confirm({
      title: turningOff ? 'Close the Lock Desk?' : 'Reopen the Lock Desk?',
      message: turningOff
        ? 'Users will not be able to submit rate locks or lock desk requests until the Lock Desk is reopened. Existing locks are not affected.'
        : 'Users will be able to submit rate locks and lock desk requests again.',
      confirmText: turningOff ? 'Close Lock Desk' : 'Reopen Lock Desk',
      cancelText: 'Cancel',
      confirmClass: turningOff ? 'btn-danger' : 'btn-success',
    });
    if (!confirmed) return;

    setIsSaving(true);
    try {
      await updateLockDeskStatus({ isEnabled: !isEnabled }).unwrap();
      showToast(turningOff ? 'Lock Desk closed — lock submission is now paused.' : 'Lock Desk reopened — lock submission is available.', 'success');
    } catch (err) {
      console.error('[LockDeskSettingsPage] update error:', err);
      showToast(getAxiosErrorMessage(err, 'Failed to update the Lock Desk status.'), 'danger');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {ToastContainer}
      {ConfirmModalContainer}

      <Stack sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Lock Desk</Typography>
        <Typography variant="body2" color="text.secondary">Pause or resume rate lock submission for all users</Typography>
      </Stack>

      {isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Unable to load the current Lock Desk status. Locking remains available to users until the status can be read. Refresh the page or try again shortly.
        </Alert>
      )}

      <Card variant="outlined" sx={{ maxWidth: 720 }}>
        <CardHeader title={<SectionHeader title="Lock Desk Status" icon={<LockClockIcon fontSize="small" />} sx={{ mb: 0 }} />} />
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={2} alignItems="center">
              <LockIcon sx={{ fontSize: 32, color: isEnabled ? 'success.main' : 'error.main' }} />
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1" fontWeight={700}>Lock Desk</Typography>
                  <StatusBadge variant={isEnabled ? 'success' : 'danger'}>{isEnabled ? 'Open' : 'Closed'}</StatusBadge>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {isEnabled
                    ? 'Users can submit rate locks and lock desk requests.'
                    : 'Lock submission is paused — users cannot submit rate locks or lock desk requests.'}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              {isSaving && <CircularProgress size={18} />}
              <Switch checked={isEnabled} onChange={handleToggle} disabled={isSaving || isError} color="success" />
            </Stack>
          </Stack>

          {!isEnabled && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              While the Lock Desk is closed, lock buttons are disabled on the loan and pipeline views and lock desk requests cannot be submitted. Existing locks remain in place.
            </Alert>
          )}

          {(status?.updatedAt || status?.updatedBy) && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" color="text.secondary">
                Last updated{status?.updatedAt ? ` ${new Date(status.updatedAt).toLocaleString()}` : ''}{status?.updatedBy ? ` by ${status.updatedBy}` : ''}
              </Typography>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default LockDeskSettingsPage;
